import type { CSSProperties } from 'react';

/**
 * WEB-1737 — стиль линии блока «Разделитель», собранный ОДНОЙ функцией.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ ИНЛАЙН В РЕНДЕРЕРЕ. Пунктир рисуется
 * повторяющимся фоном (`repeating-linear-gradient`), а тестовая среда проекта
 * (jsdom) градиенты не поддерживает и ВЫБРАСЫВАЕТ их молча: после
 * `<hr style={{ backgroundImage: 'repeating-linear-gradient(...)' }} />`
 * атрибут style у узла равен null, `hr.style.backgroundImage` — пустая строка
 * (проверено пробой 2026-08-25). То есть ни ритм штрихов, ни ЦВЕТ линии в
 * пунктирной ветке через DOM не проверяемы вовсе — сторож на разметке был бы
 * мёртвым ровно там, где живёт правка. Чистая функция проверяется напрямую:
 * возвращённый объект — те самые строки, без фильтра среды.
 *
 * КОНФЛИКТ, РАДИ КОТОРОГО ВСЁ ЗАТЕВАЛОСЬ. Линия исторически рисуется
 * ГРАНИЦЕЙ (`border-top`), а настройка «Края: скруглённые» (WEB-684 QA)
 * ставит `border-radius`, то есть скругляет углы ВСЕГО ЭЛЕМЕНТА. При
 * пунктирном стиле сегменты режет сам браузер, и на скруглённом углу крайние
 * сегменты уходят под диагональную подрезку границы: концы линии выглядят
 * обгрызенными (репорт владельца со скриншотом). Середина линии цела — потому
 * и «покусали по краям». Чем толще линия, тем больше радиус (половина
 * толщины) и тем заметнее брак; на линии в 1px его не видно вовсе — потому
 * сочетание и проехало приёмку.
 *
 * РЕШЕНИЕ (второй путь из карточки — «рисовать линию самим элементом»).
 * Пунктир СО СКРУГЛЕНИЕМ рисуем без границы: высота элемента = толщина
 * линии, штрихи = повторяющийся фон, радиус скругляет КОНЦЫ полосы — ровно
 * то, чего автор просил настройкой. Границы нет — обрезаться нечему.
 *
 * ВЕТКА УЗКАЯ НАМЕРЕННО. Сплошная линия (в том числе толстая со скруглением —
 * осознанная правка WEB-684 QA, её регресс-тесты остаются в силе) и пунктир с
 * ПРЯМЫМИ краями продолжают рисоваться границей байт-в-байт. Ритм штрихов у
 * границы задаёт браузер, у фона — мы; переводить на фон и те сочетания, где
 * брака нет, значило бы менять вид живых воронок без причины.
 *
 * Стилей линии в панели ровно два — «Сплошная» и «Пунктирная»
 * (DIVIDER_STYLE_OPTIONS в BlockPropertiesPanelExtended.tsx; тот же enum
 * объявлен и схемой бэка, content.ts). Точечного стиля в продукте нет, так
 * что вся матрица сочетаний — 2 стиля × 2 вида краёв × толщина, и она
 * перебирается сторожем целиком.
 */

export interface DividerLineFields {
  dividerStyle?: 'solid' | 'dashed';
  dividerColor?: string;
  dividerWidthPx?: number;
  dividerHeightPx?: number;
  dividerRounded?: boolean;
}

/** Умолчания WEB-684: линия во всю ширину, 1px, цвет рамки темы. */
export const DIVIDER_DEFAULT_COLOR = '#d9d9d9';
export const DIVIDER_DEFAULT_THICKNESS = 1;

/**
 * Длина штриха и промежутка для пунктира, нарисованного фоном. Кратны толщине,
 * чтобы у толстой линии рисунок не выглядел мелкой рябью, — приблизительно тот
 * же ритм, что браузер даёт `border-style: dashed`.
 */
const DASH_TO_THICKNESS = 3;
const GAP_TO_THICKNESS = 2;

export function dividerLineStyle(fields: DividerLineFields): CSSProperties {
  const thickness = fields.dividerHeightPx ?? DIVIDER_DEFAULT_THICKNESS;
  const color = fields.dividerColor ?? DIVIDER_DEFAULT_COLOR;
  // `|| 1` — наследие WEB-684: при нулевой толщине радиус не должен схлопнуться
  // в 0 и потерять смысл настройки.
  const radius = thickness / 2 || 1;
  const base: CSSProperties = {
    margin: '16px auto',
    ...(fields.dividerWidthPx ? { width: fields.dividerWidthPx } : {}),
  };

  if (fields.dividerStyle === 'dashed' && fields.dividerRounded) {
    const dash = thickness * DASH_TO_THICKNESS;
    const gap = thickness * GAP_TO_THICKNESS;
    return {
      ...base,
      borderStyle: 'none',
      height: thickness,
      borderRadius: radius,
      backgroundImage:
        `repeating-linear-gradient(to right, ` +
        `${color} 0px, ${color} ${dash}px, ` +
        `transparent ${dash}px, transparent ${dash + gap}px)`,
    };
  }

  return {
    ...base,
    borderStyle: fields.dividerStyle ?? 'solid',
    borderColor: color,
    borderWidth: `${thickness}px 0 0 0`,
    // WEB-684 QA: скруглённые края (актуально для толстых линий).
    ...(fields.dividerRounded ? { borderRadius: radius } : {}),
  };
}
